<template>
  <div>
    <div id="entry_fields">
      <div class="">
        <div class="sm:flex sm:items-center">
          <div class="sm:flex-auto">
            <h1 class="text-base font-semibold leading-6 text-gray-900">Entry Fields</h1>
            <p class="mt-2 text-sm text-gray-700">A list of all the entry fields used for these types of entries.</p>
          </div>
          <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none flex space-x-1">
            <button v-if="isSupportUser" type="button" @click="addNewFieldClicked" class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Add Field</button>
            <button v-if="isSuperUser" type="button" @click="editEntryFields" class="btn btn-link">Edit Fields</button>
          </div>
        </div>
        <div class="mt-8 flow-root">
          <div class="-mx-0 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table class="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th>
                    </th>
                    <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                      <p class="group inline-flex">
                        Field
                      </p>
                    </th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      <p class="group inline-flex">
                        Input Type
                      </p>
                    </th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      <p class="group inline-flex">
                        Hint Text
                      </p>
                    </th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      <p class="group inline-flex">
                        Required
                      </p>
                    </th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      <p class="group inline-flex">
                        Field Type
                      </p>
                    </th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      <p class="group inline-flex">
                        Admin Only
                      </p>
                    </th>
                    <th scope="col" class="relative py-3.5 pl-3 pr-0">
                      <span class="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <draggable tag="tbody" class="divide-y divide-gray-200 bg-white" :list="eventShowType.entry_details[0].fields" @start="drag=true" @end="drag=false" handle=".handle"  @change="saveEntryFields">
                  <template v-for="(s, o) in eventShowType.entry_details">
                      <tr v-for="(f, k) in s.fields" :key="`${o}-${k}`">
                        <td>
                          <div tabindex="0" role="button" aria-describedby="rbd-hidden-text-0-hidden-text-0" data-rbd-drag-handle-draggable-id="2oADuscO" data-rbd-drag-handle-context-id="0" draggable="false" class="~text-gray-500 flex h-9 w-6 items-center justify-center handle draggable"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-menu"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg></div>
                        </td>
                        <td class="whitespace-nowrap py-2.5 pl-3 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                          <div class="flex flex-col">
                            <div>{{ f.label }}</div>
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-3 py-2.5 text-sm text-gray-700" v-if="f.type">{{(f.type === 'input' ? (f.inputType ? f.inputType : 'missing') : f.type).toUpperCase()}}</td>
                        <td class="whitespace-nowrap px-3 py-2.5 text-sm text-gray-700">{{ f.hint }}</td>
                        <td class="whitespace-nowrap px-3 py-2.5 text-sm text-gray-700">{{ f.required ? 'Yes' : 'No' }}</td>
                        <td class="whitespace-nowrap px-3 py-2.5 text-sm text-gray-700">
                          <span v-if="getFieldType(f) === 'EXHIBITOR' || f.exhibitor_field" class="inline-flex items-center rounded-md bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700 border border-green-200">Exhibitor</span>
                          <span v-else-if="getFieldType(f) === 'CUSTOM'" class="inline-flex items-center rounded-md bg-yellow-100 px-1.5 py-0.5 text-xs font-medium text-yellow-700 border border-yellow-400">Custom</span>
                          <span v-else-if="getFieldType(f) === 'SPECIES'" class="inline-flex items-center rounded-md bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 border border-blue-200">Animal</span>
                          <span v-else-if="getFieldType(f) === 'SERIES'" class="inline-flex items-center rounded-md bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-700 border border-orange-200">Series</span>
                        </td>
                        <td class="whitespace-nowrap px-3 py-2.5 text-sm text-gray-700">{{ f.admin_only ? 'Yes' : 'No'}}</td>
                        <td class="relative whitespace-nowrap py-2.5 pl-3 pr-4 text-right text-sm sm:pr-0 flex items-center space-x-2">
                          <template v-if="(f.model !== 'animal_id' || isSuperUser) && isSupportUser && (!f.series_field || isSuperUser)">
                            <button class="text-blue-600 hover:text-blue-900" @click="editEntryFieldClicked(f)">
                              Edit<span class="sr-only">, {{ f.name }}</span>
                            </button>
                            <button @click="removeEntryField(s, k)" type="button" class="flex h-8 w-9 shrink-0 items-center justify-center text-red-600 hover:text-red-700">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-trash-2">
                                <polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          </template>
                          <div v-else class="flex h-8 items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
                              <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clip-rule="evenodd" />
                            </svg>
                          </div>
                        </td>
                      </tr>
                  </template>
                </draggable>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <b-modal v-if="eventShowType" ref="entryFields" :title="`Show Type`" no-close-on-backdrop hide-footer @close="cancelEntryFieldEdit">
      <v-jsoneditor v-model="eventShowType.entry_details" :options="{mode:'code', modes:['code', 'text'], sortObjectKeys: false}" :plus="false" height="800px" class="mt-4"/>
      <div class="flex justify-end space-x-3 items-center mt-2">
        <button @click="saveEntryFields" class="btn btn-green">Save</button>
        <button @click="cancelEntryFieldEdit" class="btn-xs btn-red-outline">Cancel</button>
      </div>
    </b-modal>

    <b-modal ref="entryFieldModal" :title="`${selectedEntryField.new_field ? 'New' : 'Edit'} Entry Field`" @close="cancelAddNewField" hide-footer>
      <div id="editEntryField" v-if="selectedEntryField" class="">
        <div class="flex justify-start items-center">
          <p class="mb-4">
            <span v-if="selectedFieldType === 'CUSTOM'" class="inline-flex items-center rounded-md bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 border border-yellow-400">Custom Field</span>
            <span v-else-if="selectedFieldType === 'SPECIES'" class="inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 border border-blue-400">Animal Field</span>
            <span v-else-if="selectedFieldType === 'EXHIBITOR'" class="inline-flex items-center rounded-md bg-green-100 px-2 py-1 text-xs font-medium text-green-800 border border-green-400">Exhibitor Field</span>
          </p>
        </div>
        <div v-if="selectedEntryField.new_field" class="">
          <div class="mb-4">
            <p class="text-lg mb-2">What type of field do you want to add?</p>
            <select v-model="newFieldType"  class="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 border border-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
              <option disabled :value="null">Please select a field type</option>
              <option value="ANIMAL">Animal Field</option>
              <option value="EXHIBITOR">Exhibitor Field</option>
            </select>
            <template v-if="newFieldType">
              <select v-if="newFieldType === 'ANIMAL'" v-model="selectedEntryField.newFieldType" class="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 border border-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                <option selected disabled :value="null">Please select the animal field...</option>
                <option v-for="(f, i) in entryFields" :key="i" :value="f" :disabled="doesFieldExists(f)">{{f.label}}</option>
                <option value="CUSTOM">Add a custom field</option>
              </select>
              <select v-else-if="newFieldType === 'EXHIBITOR'" v-model="selectedEntryField.newFieldType" class="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 border border-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                <option selected disabled :value="null">Please select the exhibitor field...</option>
                <option v-for="(f, i) in exhibitorFields" :key="i" :value="f" :disabled="doesFieldExists(f)">{{f.label}}</option>
                <option value="CUSTOM">Add a custom field</option>
              </select>
            </template>
          </div>
          <div class="flex justify-end">
            <div class="flex">
              <button class="btn btn-link" @click="cancelAddNewField">Cancel</button>
              <button v-if="selectedEntryField.newFieldType" class="btn-sm btn-green" @click="addNewField">Add Field</button>
            </div>
          </div>
        </div>
        <div v-else class="flex items-center space-x-2">
          <div class="w-full flex-col flex  md:flex-row justify-between items-start">
            <div class="w-full flex-1">
              <div class="flex mb-4" :class="{'flex-col_': selectedFieldType === 'CUSTOM', 'items-center_': selectedFieldType !== 'CUSTOM'}">
                <div class="flex-1 mr-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Field Label</label>
                  <template v-if="selectedFieldType === 'CUSTOM'">
                    <input type="text" class="form-input focus-within:ring-indigo-600 focus-within:border-indigo-600" placeholder="Field Label" v-model="selectedEntryField.label">
                  </template>
                  <p v-else>
                    {{ selectedEntryField.label }}
                  </p>
                </div>
                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Input Type</label>
                  <template v-if="selectedFieldType === 'CUSTOM'">
                    <v-select
                      class="bg-white rounded flex-1 w-56 mb-2"
                      v-model="selectedEntryField.selectType"
                      :options="fieldTypes"
                      label="label"
                      :reduce="option => option.value"
                      :clearable="false"
                      @input="checkInputType(selectedEntryField)"
                    >
                      <template #option="{ description, label }">
                        <p class="font-medium md:text-lg">{{ label }}</p>
                        <p class='text-xs md:text-sm whitespace-pre-wrap pb-2'>{{description}}</p>
                      </template>
                      <template #selected-option="{ label }">
                        <div class="p-1" style="display: flex; align-items: baseline">
                          <strong>{{ label }}</strong>
                        </div>
                      </template>
                    </v-select>
                  </template>
                  <p v-else>
                    {{ fieldTypes.filter(ft => ft.value === selectedEntryField.selectType)[0].label }}
                  </p>
                </div>
              </div>
              <div v-if="selectedEntryField.type === 'select'" class="mb-4">
                <div v-if="selectedFieldType === 'CUSTOM'" class="py-3 border-t border-b">
                  <p class="font-bold">Select Options:</p>
                  <div class="mt-2">
                    <div v-for="(o, j) in selectedEntryField.options" :key="j">
                      <div class="flex items-center space-x-2">
                        <div class="flex-1">
                          <label class="block text-sm font-medium text-gray-700 mb-1">Label</label>
                          <input type="text" class="form-input focus-within:ring-indigo-600 focus-within:border-indigo-600" placeholder="Field Label" v-model="o.label">
                        </div>
                        <div class="flex-1">
                          <label class="block text-sm font-medium text-gray-700 mb-1">Value</label>
                          <input type="text" class="form-input focus-within:ring-indigo-600 focus-within:border-indigo-600" placeholder="Field Label" v-model="o.value">
                        </div>
                        <button @click="selectedEntryField.options.splice(j,1)" type="button" class="flex h-8 w-9 shrink-0 items-center justify-center text-red-600 hover:text-red-700">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-trash-2">
                            <polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div class="py-3">
                      <button @click="addNewOption(selectedEntryField)" type="button" class="text-blue-600 flex items-center space-x-2 text-sm font-medium focus:outline-none focus:ring">
                        <div class="flex w-6 justify-center">
                          <div><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-plus"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg></div>
                        </div>
                        <div>Add an option</div>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="bg-gray-200 px-2 py-4 rounded flex space-x-1">
                  <p class="font-bold">Select Options:</p>
                  <div v-for="(o, j) in selectedEntryField.options" :key="j">
                    {{ o.label }}<span v-if="j !== selectedEntryField.options.length-1">,</span>
                  </div>
                </div>
              </div>
              <div class="flex items-center space-x-2 mb-4">
                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-700  mb-1">Required</label>
                  <input v-if="selectedEntryField.model !== eventShowType.class_calc.animal_id_field" v-model="selectedEntryField.required" class="mr-2 leading-tight" type="checkbox">
                  <p v-else class="text-sm">Yes - This field is set as the animal ID Field.</p>
                </div>
                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-700  mb-1">Admin Only</label>
                  <input v-model="selectedEntryField.admin_only" class="mr-2 leading-tight" type="checkbox">
                </div>
              </div>
              <div class="w-full flex items-top space-x-2">
                <div class="flex-1">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Hint Text (Optional)</label>
                  <textarea class="form-input focus-within:ring-indigo-600 focus-within:border-indigo-600" rows="3" placeholder="Add some text to help out the exhibitor..." v-model="selectedEntryField.hint"></textarea>
                </div>
              </div>
              <div class="flex justify-end">
                <button class="btn btn-link" @click="cancelEditEntryField">Cancel</button>
                <button class="btn btn-green" @click="saveSelectedEntryField">Save</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import Fuse from 'fuse.js'

import draggable from 'vuedraggable'
import VJsoneditor from 'v-jsoneditor'

export default {
  components: {
    draggable,
    VJsoneditor
  },
  props: {
    eventShowType: Object,
    entryFields: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      selectedEntryField: {},
      exhibitorFields: [
        {
          'model': 'date_of_birth',
          'label': 'Date of Birth',
          'type': 'DATE',
          'options': null,
          'notes': null
        },
        {
          'model': 'club',
          'label': 'Club',
          'type': 'TEXT',
          'options': null,
          'notes': null
        },
        {
          'model': '4h_club',
          'label': '4-H Club',
          'type': 'TEXT',
          'options': null,
          'notes': null
        },
        {
          'model': 'ffa_chapter',
          'label': 'FFA Chapter',
          'type': 'TEXT',
          'options': null,
          'notes': null
        },
        {
          'model': 'county',
          'label': 'County',
          'type': 'TEXT',
          'options': null,
          'notes': null
        }
      ],
      fieldTypes: [
        {
          value: 'input:text',
          label: 'Text',
          description: 'Provide a text field for exhibitors to input the info on their entry.'
        },
        {
          value: 'input:number',
          label: 'Number',
          description: 'Provide a number box for exhibtiors to input the info on their entry.'
        },
        {
          label: 'Date',
          value: 'input:date',
          description: 'Provide a date picker for exhibtiors to input the info on their entry.'
        },
        {
          value: 'checkbox',
          label: 'Checkbox',
          description: 'Provide a checbox for exhibtiors to check if applicable.'
        },
        {
          label: 'Select',
          value: 'select',
          description: 'Provide a dropdown box for exhibitors to select their answer from a list of options.'
        },
        {
          label: 'File',
          value: 'file',
          description: 'Provide an upload widget for exhibitors to upload a file (Only Image and PDF formats accepted)'
        }
      ],
      newFieldType: null
    }
  },
  computed: {
    currentUser () {
      return this.$store.state.user
    },
    isSuperUser () {
      return this.currentUser.user_id === 'Clo1Va5v4pNuvE1TqHPNeKWFGay2'
    },
    event () {
      return this.$store.state.admin.adminData.event
    },
    isAssesmentUser () {
      if (this.currentUser.user_id === 'KFtUZcXZ1ITCICo8w0kZdJHB65n1' && this.event.event_id === 'ce97824c-1df8-11ef-8e5d-1248ddbb6de9') {
        return true
      } else if (this.currentUser.user_id === 'PaFB62Hpp4V3BlrUFebP3MRNi0g2' && this.event.event_id === '1d7af7ce-1e10-11ef-b2db-1248ddbb6de9') {
        return true
      } else if (this.currentUser.user_id === 'MhDTB3YTmTgMZB9g2lVoflY1luO2' && this.event.event_id === '636ee696-1e10-11ef-ad80-1248ddbb6de9') {
        return true
      } else if (this.currentUser.user_id === 'gsXtVouX3HUQZM8qvrVTa2efLOK2' && this.event.event_id === 'a3e57334-1e10-11ef-a269-1248ddbb6de9') {
        return true
      } else if (this.currentUser.user_id === '3PyICPP8s3OPx7F7BIvV3kRx1OD3' && this.event.event_id === 'bd174d14-1e10-11ef-bd1d-1248ddbb6de9') {
        return true
      } else if (this.currentUser.user_id === 'ECXhYgYtmnUmN8i19kP5vZp5Ulj2' && this.event.event_id === '6f670c58-e7ba-11ee-b65a-1248ddbb6de9') {
        return true
      } else {
        return false
      }
    },
    isSupportUser () {
      return this.currentUser.role === 'ADMIN' || this.isSuperUser || this.isAssesmentUser
    },
    selectedFieldType () {
      return this.getFieldType(this.selectedEntryField)
    }
  },
  methods: {
    addNewFieldClicked () {
      this.newFieldType = null
      this.selectedEntryField = {
        new_field: true,
        newFieldType: null
      }
      this.$refs.entryFieldModal.show()
    },
    editEntryFields () {
      this.originalEntryFields = JSON.parse(JSON.stringify(this.eventShowType.entry_details))
      this.$refs.entryFields.show()
    },
    cancelEntryFieldEdit () {
      this.eventShowType.entry_details = this.originalEntryFields
      this.$refs.entryFields.hide()
    },
    saveEntryFields () {
      // this.saveEventShowType()
      // Add a callback here...
      this.$emit('save')
      this.$refs.entryFields.hide()
    },
    addInputType (field) {
      if (field.type === 'input') {
        if (field.inputType === 'text') {
          field.selectType = 'input:text'
        } else if (field.inputType === 'date') {
          field.selectType = 'input:date'
        } else if (field.inputType === 'number') {
          field.selectType = 'input:number'
        }
      } else {
        field.selectType = field.type
      }
    },
    checkInputType (field) {
      let type = field.selectType.split(':')[1]
      if (type === 'text' || type === 'date' || type === 'number') {
        field.inputType = type
        field.type = 'input'
      } else {
        field.type = field.selectType
      }
    },
    getFieldType (f) {
      if (f.new_field) {
        return 'NEW'
      } else if (f.series_field) {
        return 'SERIES'
      } else if (f.exhibitor_field) {
        return 'EXHIBITOR'
      } else if (this.entryFields.filter(ef => ef.model === f.model).length > 0) {
        return 'SPECIES'
      } else {
        return 'CUSTOM'
      }
    },
    editEntryFieldClicked (f) {
      this.$refs.entryFieldModal.show()
      this.selectedEntryField = JSON.parse(JSON.stringify(f))
      this.addInputType(this.selectedEntryField)
    },
    checkIfDuplicate (label) {
      let fields = []
      this.entryFields.forEach(ef => {
        fields.push(ef.label)
      })
      const fuse = new Fuse(
        fields.map(label => ({label})),
        {
          keys: ['label'],
          threshold: 0.15,
          caseSensitive: false,
          minMatchCharLength: 3,
          ignoreLocation: true,
          includeScore: true,
          findAllMatches: true
        }
      )

      const searchResult = fuse.search({
        label: label
      })

      this.isDuplicate = searchResult.length > 0
      return searchResult
    },
    saveSelectedEntryField () {
      // Check for duplicate Fields in custom types
      if (this.selectedFieldType === 'CUSTOM') {
        // Check if field exists:
        if (this.eventShowType.entry_details[0].fields.filter(f => f.label.toLowerCase() === this.selectedEntryField.label.toLowerCase() && f.model !== this.selectedEntryField.model).length > 0) {
          this.notie.alert({
            type: 3,
            text: `Field already exists.`
          })
          return
        }
        // Check for a reserved field
        let dupResult = this.checkIfDuplicate(this.selectedEntryField.label)
        if (dupResult.length > 0) {
          this.notie.alert({
            type: 3,
            text: `Error - "${this.selectedEntryField.label}" is a reserved field. Please choose to add the "${dupResult[0].item.label}" field from the list of available fields. Instead of creating a new custom field.`
          })
          return
        }
      }

      // Check if it is the id field that it is required:
      if (this.selectedEntryField.model === this.eventShowType.class_calc.animal_id_field) {
        if (!this.selectedEntryField.required) {
          this.notie.alert({
            type: 3,
            time: 5,
            text: `Error - "${this.selectedEntryField.label}" is currenlty set as your main animal ID field. Please make sure it is required or choose a different field to be the main animal ID field.`
          })
          return
        }
      }

      let index = this.eventShowType.entry_details[0].fields.findIndex(f => f.model === this.selectedEntryField.model)
      if (index === -1) {
        this.eventShowType.entry_details[0].fields.push(this.selectedEntryField)
      } else {
        this.eventShowType.entry_details[0].fields.splice(index, 1, this.selectedEntryField)
      }
      this.saveEntryFields()
      this.selectedEntryField = {}
      this.$refs.entryFieldModal.hide()
    },
    cancelEditEntryField () {
      this.selectedEntryField = {}
      this.$refs.entryFieldModal.hide()
    },
    removeEntryField (section, index) {
      if (section.fields[index].model === this.eventShowType.class_calc.animal_id_field) {
        this.notie.alert({
          type: 3,
          text: `You cannot remove the "${section.fields[index].label}" field because it is being used as the Animal ID field for this entry type.`
        })
        return
      }

      this.notie.confirm({
        text: `Are you sure you want to remove the "${section.fields[index].label}"" entry field?`,
        submitCallback: () => {
          section.fields.splice(index, 1)
          this.saveEntryFields()
        }
      })
    },
    addNewField () {
      // Make sure enough data for entry fields is initialized
      if (!this.eventShowType.entry_details[0]) {
        this.eventShowType.entry_details.push({})
      }
      if (!this.eventShowType.entry_details[0].fields) {
        Vue.set(this.eventShowType.entry_details[0], 'fields', [])
      }

      if (this.selectedEntryField.newFieldType === 'CUSTOM') {
        // this.value[0].fields.push({})
        this.selectedEntryField = {
          model: new Date().valueOf(),
          required: true
        }
      } else {
        let field = {}
        if (this.newFieldType === 'EXHIBITOR') {
          field = this.selectedEntryField.newFieldType // this.exhibitorFields.filter(ef => ef.model === this.selectedEntryField.newFieldType)[0]
        } else {
          field = this.selectedEntryField.newFieldType // this.entryFields.filter(ef => ef.model === this.selectedEntryField.newFieldType)[0]
        }
        let fieldObject = {
          model: field.model,
          label: field.label,
          exhibitor_field: this.newFieldType === 'EXHIBITOR',
          required: true
        }
        if (field.type === 'SELECT') {
          fieldObject.type = 'select'
          fieldObject.selectType = 'select'
          fieldObject.options = []
          field.options.forEach(o => {
            fieldObject.options.push({
              value: o,
              label: o
            })
          })
        } else if (field.type === 'CHECKBOX') {
          fieldObject.type = 'checkbox'
          fieldObject.selectType = 'checkbox'
        } else if (field.type === 'FILE') {
          fieldObject.type = 'file'
          fieldObject.selectType = 'file'
        } else {
          fieldObject.type = 'input'
          fieldObject.selectType = `input:${field.type.toLowerCase()}`
        }
        this.checkInputType(fieldObject)
        this.selectedEntryField = fieldObject
      }
    },
    cancelAddNewField () {
      this.selectedEntryField = {}
      this.$refs.entryFieldModal.hide()
    },
    doesFieldExists (f) {
      return this.eventShowType.entry_details[0].fields.filter(ef => ef.model === f.model).length > 0
    },
    addNewOption (field) {
      if (!field.options) {
        Vue.set(field, 'options', [])
      }
      field.options.push({})
    }
  }
}
</script>

<style>

</style>
